'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';

import {
	CAREER_OFFER_EVENTS,
	getCareerOfferRouteContext,
	getCareerOfferTechnology,
	useCareerOfferAnalytics,
} from '@/features/career-offer-analytics';

import {
	createCareerOfferEngagementTracker,
	getCareerOfferInteractionEvent,
} from '../model/engagement';

export function OfferAnalyticsTracker() {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const { trackEvent } = useCareerOfferAnalytics();
	const trackEventRef = useRef(trackEvent);
	trackEventRef.current = trackEvent;
	const technology = getCareerOfferTechnology(pathname);
	const query = searchParams.toString();
	const routeContext = useMemo(
		() => getCareerOfferRouteContext(new URLSearchParams(query)),
		[query],
	);
	const commonContext = useMemo(
		() => ({ ...(technology && { technology }), ...routeContext }),
		[routeContext, technology],
	);

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			if (!(event.target instanceof Element)) return;
			const target = event.target.closest<HTMLElement>('[data-offer-action]');
			const action = target?.dataset.offerAction;
			if (!target || !action) return;
			const name = getCareerOfferInteractionEvent(action);
			if (!name) return;

			trackEventRef.current({
				name,
				...commonContext,
				...(target.dataset.offerPlacement && {
					placement: target.dataset.offerPlacement,
				}),
				...(target.dataset.offerValue && {
					value: target.dataset.offerValue,
				}),
			});
		};

		document.addEventListener('click', handleClick);
		return () => document.removeEventListener('click', handleClick);
	}, [commonContext, pathname]);

	useEffect(() => {
		if (!('IntersectionObserver' in window)) return;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (!entry.isIntersecting) continue;
					const section = (entry.target as HTMLElement).dataset.offerSection;
					if (!section) continue;
					trackEventRef.current({
						name: CAREER_OFFER_EVENTS.SECTION_VIEW,
						...commonContext,
						section,
					});
					observer.unobserve(entry.target);
				}
			},
			{ threshold: 0.1 },
		);

		const sections = document.querySelectorAll<HTMLElement>(
			'[data-offer-section]',
		);
		sections.forEach((section) => observer.observe(section));
		return () => observer.disconnect();
	}, [commonContext, pathname]);

	useEffect(() => {
		if (!technology || technology === 'other') return;

		const tracker = createCareerOfferEngagementTracker({
			emitDepth: (depth) =>
				trackEventRef.current({
					name: CAREER_OFFER_EVENTS.SCROLL_DEPTH,
					...commonContext,
					value: String(depth),
				}),
			emitTime: (seconds) =>
				trackEventRef.current({
					name: CAREER_OFFER_EVENTS.ENGAGED_VISIT,
					...commonContext,
					value: String(seconds),
				}),
		});
		const updateScroll = () => {
			const scrollHeight = document.documentElement.scrollHeight;
			const progress = scrollHeight
				? (window.scrollY + window.innerHeight) / scrollHeight
				: 0;
			tracker.updateScroll(progress);
		};
		const interval = window.setInterval(() => {
			if (document.visibilityState === 'visible') {
				tracker.advanceVisibleTime(1);
			}
		}, 1000);

		updateScroll();
		window.addEventListener('scroll', updateScroll, { passive: true });
		window.addEventListener('resize', updateScroll);
		return () => {
			window.clearInterval(interval);
			window.removeEventListener('scroll', updateScroll);
			window.removeEventListener('resize', updateScroll);
		};
	}, [commonContext, pathname, technology]);

	return null;
}
